import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import SessionParticipants, { type SessionParticipant } from './SessionParticipants'
import { Session } from '../features/sessions/models/Session'
import { InviteeType } from '../features/sessions/models/InviteeType'
import { formatLocalTimeRange } from '../features/utils/timeFormatter'
import { ROUTES } from '../routes'

export type SessionCardData = {
  id?: string
  title: string
  times: string[]
  variant?: 'aqua' | 'orange'
  participants?: SessionParticipant[]
}

type SessionCardProps = {
  session: Session
  borderClass?: string
}

const SessionCard: React.FC<SessionCardProps> = ({ session, borderClass }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleDetailsClick = () => {
    navigate(ROUTES.SESSION_DETAILS.replace(':id', session.id));
  };

  const handleEditClick = () => {
    navigate(ROUTES.EDIT_SESSION.replace(':id', session.id), {
      state: { from: location.pathname }
    });
  };

  // Format times using timezone-aware utility
  const timeRange = formatLocalTimeRange(session.timeLocal, session.duration);
  const times = [timeRange];

  // Map invitees to participants
  const participants: SessionParticipant[] = session.invitees.map(invitee => ({
    name: `User ${invitee.invitedUserId.substring(0, 8)}`, // Placeholder until we have actual names
    type: invitee.type === InviteeType.Group ? 'group' as const : 'individual' as const,
    count: invitee.type === InviteeType.Group ? 1 : undefined, // Placeholder
  }));

  // Determine variant based on session id
  const variant = session.id.length % 2 === 0 ? 'orange' : 'aqua';

  const variantClass = variant === 'aqua' ? 'border-aqua' : variant === 'orange' ? 'border-orange' : ''
  const combinedBorderClass = [variantClass, borderClass].filter(Boolean).join(' ').trim()
  return (
    <div className="with-border flex overflow-hidden rounded-2xl">
      <div className="flex w-full flex-col gap-2 md:flex-row md:items-center">
        <div className={`event-details-info m-0 flex w-full flex-col gap-2 border-l-[8px] px-2 py-3 md:flex-row md:items-center md:gap-3 md:p-4 ${combinedBorderClass}`.trim()}>
          <p className="line-clamp-1 font-semibold md:w-full md:max-w-[200px]">{session.title}</p>
          <div className="flex flex-wrap items-center gap-2 md:min-w-[130px] md:flex-col md:gap-0">
            {times.slice(0, 2).map((t, idx) => (
              <p
                key={idx}
                className="text-grey-30 dark:text-grey-70 border-r-grey-70 line-clamp-1 max-w-fit border-r pr-2 text-sm last:border-r-0 md:border-r-0 md:pr-0"
              >
                {t}
              </p>
            ))}
          </div>
          {participants ? (
            <SessionParticipants participants={participants} />
          ) : null}
        </div>
      </div>
      <div className="border-l-grey-70 flex h-auto w-fit items-center justify-center border-l md:px-2">
        <button onClick={handleEditClick} className="btn btn-text h-full px-2">
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
              fill="#4D4D4D"
            />
          </svg>
          <span className="hidden md:block">Edit</span>
        </button>
        <div className="border-l-grey-70 border-l w-px h-8"></div>
        <button onClick={handleDetailsClick} className="btn btn-text h-full px-2">
          <span className="hidden md:block">Details</span>
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.75781 7.03029C10.1043 6.93744 10.4749 7.03639 10.7285 7.29005L14.6982 11.2901C14.8875 11.4778 14.9941 11.7334 14.9941 12C14.9941 12.2666 14.8875 12.5222 14.6982 12.71L10.6982 16.71C10.512 16.8947 10.2604 16.9989 9.99805 17C9.73224 17.0015 9.4768 16.8972 9.28809 16.71C9.0988 16.5222 8.99219 16.2666 8.99219 16C8.99221 15.7334 9.09879 15.4778 9.28809 15.2901L12.5781 12L9.30762 8.70997C9.05419 8.45635 8.95509 8.08659 9.04785 7.74025C9.14067 7.39386 9.41148 7.12322 9.75781 7.03029Z"
              fill="#4D4D4D"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default SessionCard
